import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spinner,
  Text,
  Badge,
  Caption1,
  ToolbarButton,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  ArrowDownloadRegular,
  LinkRegular,
  DocumentCopyRegular,
  MoreHorizontalRegular,
} from '@fluentui/react-icons';
import { DrawioFile } from './FileList';
import { getFileContent, saveFileContent, getFreshDownloadUrl } from '../services/graphApi';
import { getDrawioOpenFromUrl, getDrawioOneDriveModeUrl, getDrawioSharePointDirectUrl } from '../utils/drawioLinks';

interface DrawioViewerProps {
  file: DrawioFile;
  teamId: string;
  onBack: () => void;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * DrawioViewer – displays and edits a Draw.io file directly in an embedded iframe.
 *
 * Flow:
 * 1. File content is loaded via Graph API (XML text).
 * 2. Content is passed to the diagrams.net embed mode via postMessage.
 * 3. On changes (autosave event), the XML is automatically saved back to SharePoint.
 */
export const DrawioViewer: React.FC<DrawioViewerProps> = ({ file, teamId, onBack }) => {
  const { t } = useTranslation();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileXml, setFileXml] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [openingInDrawio, setOpeningInDrawio] = useState(false);
  const [showCollaborationHint, setShowCollaborationHint] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestXmlRef = useRef<string | null>(null);

  // Load file content
  useEffect(() => {
    let cancelled = false;

    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const xml = await getFileContent(teamId, file.id);
        if (!cancelled) {
          setFileXml(xml);
          latestXmlRef.current = xml;
        }
      } catch (err: any) {
        console.error('[DrawioViewer] Fehler beim Laden:', err);
        if (!cancelled) {
          setError(err?.message || t('drawio.loadFileError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadContent();
    return () => { cancelled = true; };
  }, [teamId, file.id]);

  // Save with debounce – waits 1.5s after last change
  const debouncedSave = useCallback(
    (xml: string) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      setHasUnsavedChanges(true);
      latestXmlRef.current = xml;

      saveTimeoutRef.current = setTimeout(async () => {
        try {
          setSaveStatus('saving');
          await saveFileContent(teamId, file.id, xml);
          setSaveStatus('saved');
          setHasUnsavedChanges(false);
          // Hide "Saved" indicator after 3s
          setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (err: any) {
          console.error('[DrawioViewer] Fehler beim Speichern:', err);
          setSaveStatus('error');
        }
      }, 1500);
    },
    [teamId, file.id]
  );

  // Manual save
  const handleManualSave = useCallback(async () => {
    const xml = latestXmlRef.current;
    if (!xml) return;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    try {
      setSaveStatus('saving');
      await saveFileContent(teamId, file.id, xml);
      setSaveStatus('saved');
      setHasUnsavedChanges(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: any) {
      console.error('[DrawioViewer] Fehler beim Speichern:', err);
      setSaveStatus('error');
    }
  }, [teamId, file.id]);

  // Cleanup Timeout
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // postMessage handler for communication with diagrams.net embed
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (!iframeRef.current) return;

      let msg: any;
      try {
        msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return; // Ignore non-JSON message
      }

      // diagrams.net sends "init" when the editor is ready
      if (msg.event === 'init') {
        console.log('[DrawioViewer] diagrams.net bereit – sende XML …');
        const loadMsg = JSON.stringify({
          action: 'load',
          xml: fileXml,
          autosave: 1,
        });
        iframeRef.current.contentWindow?.postMessage(loadMsg, '*');
      }

      // autosave: diagrams.net sends a "save" event on every change
      if (msg.event === 'autosave') {
        console.log('[DrawioViewer] Autosave-Event empfangen – speichere …');
        debouncedSave(msg.xml);
      }

      // Explicit save (Ctrl+S in editor)
      if (msg.event === 'save') {
        console.log('[DrawioViewer] Save-Event empfangen – speichere sofort …');
        latestXmlRef.current = msg.xml;
        handleManualSave();
      }

      // Export event (sent after manual "Save" in editor)
      if (msg.event === 'export') {
        console.log('[DrawioViewer] Export-Event empfangen');
        if (msg.data) {
          latestXmlRef.current = msg.data;
          handleManualSave();
        }
      }
    },
    [fileXml, debouncedSave, handleManualSave]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Warn before leaving when there are unsaved changes
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      const confirm = window.confirm(t('drawio.unsavedConfirm'));
      if (!confirm) return;
    }
    onBack();
  }, [hasUnsavedChanges, onBack, t]);

  const openInDrawioCopy = useCallback(async () => {
    try {
      setOpeningInDrawio(true);
      const downloadUrl = await getFreshDownloadUrl(teamId, file.id);
      window.open(getDrawioOpenFromUrl(downloadUrl), '_blank');
    } catch (err: any) {
      console.error('[DrawioViewer] Fehler beim Öffnen in draw.io:', err);
      window.alert(err?.message || t('drawio.linkError'));
    } finally {
      setOpeningInDrawio(false);
    }
  }, [teamId, file.id, t]);

  const openInDrawioCollaboration = useCallback(() => {
    if (file.driveId) {
      // Direct link: draw.io opens the file via /drives/{driveId}/items/{itemId}
      window.open(getDrawioSharePointDirectUrl(file.driveId, file.id), '_blank');
    } else {
      // Fallback: OneDrive mode – user must navigate manually
      window.open(getDrawioOneDriveModeUrl(), '_blank');
      setShowCollaborationHint(true);
    }
  }, [file.driveId, file.id]);

  const downloadFile = async () => {
    try {
      // Use current XML file (with all changes)
      const xml = latestXmlRef.current || fileXml;
      if (!xml) {
        console.error('[DrawioViewer] Keine XML-Daten verfügbar');
        return;
      }
      
      // Create blob from XML and trigger download
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[DrawioViewer] Fehler beim Herunterladen:', error);
    }
  };

  const renderSaveStatus = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <Badge appearance="outline" color="informative" className="drawio-save-badge">
            <Spinner size="tiny" style={{ marginRight: 4 }} /> {t('drawio.saving')}
          </Badge>
        );
      case 'saved':
        return (
          <Badge appearance="filled" color="success" className="drawio-save-badge">
            <CheckmarkCircleRegular style={{ marginRight: 4, fontSize: 14 }} /> {t('drawio.saved')}
          </Badge>
        );
      case 'error':
        return (
          <Badge appearance="filled" color="danger" className="drawio-save-badge" onClick={handleManualSave} style={{ cursor: 'pointer' }}>
            <ErrorCircleRegular style={{ marginRight: 4, fontSize: 14 }} /> {t('drawio.errorRetry')}
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="drawio-viewer-container">
        <div className="drawio-viewer-loading">
          <Spinner size="large" label={t('drawio.loadingFile', { name: file.name })} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="drawio-viewer-container">
        <div className="drawio-viewer-toolbar">
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={onBack}
          >
            {t('drawio.back')}
          </Button>
        </div>
        <div className="drawio-viewer-error">
          <Text size={500} weight="semibold">
            ⚠️ {t('drawio.loadError')}
          </Text>
          <Text size={400} style={{ marginTop: '12px' }}>
            {error}
          </Text>
          <Button
            appearance="primary"
            style={{ marginTop: '16px' }}
            onClick={onBack}
          >
            {t('drawio.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  // diagrams.net embed URL – editor mode with autosave
  const embedUrl = 'https://embed.diagrams.net/?embed=1&proto=json&spin=1&ui=min&noExitBtn=1&saveAndExit=0&noSaveBtn=0&modified=unsavedChanges';

  return (
    <div className="drawio-viewer-container">
      <div className="drawio-viewer-toolbar">
        <div className="drawio-toolbar-left">
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={handleBack}
          >
            Zurück
          </Button>
          <Text weight="semibold" className="drawio-filename">
            {file.name}
          </Text>
          {renderSaveStatus()}
        </div>
        <div className="drawio-toolbar-right">
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <ToolbarButton
                icon={<MoreHorizontalRegular />}
                title="Weitere Aktionen"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<LinkRegular />}
                  onClick={openInDrawioCollaboration}
                >
                  In draw.io öffnen (Kollaboration)
                </MenuItem>
                <MenuItem
                  icon={<DocumentCopyRegular />}
                  disabled={openingInDrawio}
                  onClick={openInDrawioCopy}
                >
                  {openingInDrawio ? 'Öffnet…' : 'Als Kopie in draw.io öffnen'}
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  icon={<ArrowDownloadRegular />}
                  onClick={downloadFile}
                >
                  Datei herunterladen
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>
      {showCollaborationHint && (
        <div
          role="region"
          aria-live="polite"
          style={{
            padding: '8px 12px',
            margin: '0 12px 8px',
            background: 'var(--colorNeutralBackground3)',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <Caption1 style={{ color: 'var(--colorNeutralForeground1)' }}>
            {t('drawio.collaborationHint', {
              path: file.path ? file.path + ' → ' : '',
              name: file.name,
            })}
          </Caption1>
          <Button
            appearance="subtle"
            size="small"
            onClick={() => setShowCollaborationHint(false)}
          >
            {t('drawio.close')}
          </Button>
        </div>
      )}
      <div className="drawio-viewer-frame-wrapper">
        <iframe
          ref={iframeRef}
          src={embedUrl}
          className="drawio-viewer-iframe"
          title={t('drawio.editorTitle', { name: file.name })}
          frameBorder="0"
          allow="fullscreen"
        />
      </div>
    </div>
  );
};
